import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Stack,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Typography,
} from '@mui/joy';
import { ClientInformation } from '@/components/Pages/Clients/ClientInformation';
import { CreateEditClientModal } from '@/components/Pages/Clients/CreateEditClientModal';
import { createFileRoute, Link, useNavigate, Navigate } from '@tanstack/react-router';
import { pageTitle, usePageTitle } from '@/utils/pageTitle';
import { CustomerDepositModal } from '@/components/common/CustomerDepositModal';
// import { DeleteClientModal } from '@/components/Pages/Clients/DeleteClientModal';
import { LedgerTable } from '@/components/Pages/Accounts/LedgerTable';
import { MultiplePaymentsModal } from '@/components/Pages/Sells/MultiplePaymentsModal';
import { SellTable } from '@/components/Pages/Sells/SellTable';
import { useClient } from '@/hooks/useClients';
import { useState } from 'react';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';

export const Route = createFileRoute('/_protected/clients/$id')({
  component: ClientDetail,
  head: () => pageTitle('Clientes'),
  validateSearch: z.object({
    tab: z.number().default(0),
  }),
});

function ClientDetail() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { hasClaim, isLoading: authLoading } = useAuth();
  const canRead = hasClaim('CLIENTS_READ');
  const canWrite = hasClaim('CLIENTS_WRITE');

  const { data: client, isLoading } = useClient(id);
  usePageTitle('Clientes', client?.name);

  const [selectedTab, setSelectedTab] = useState(tab);

  const handleTabChange = (value: number) => {
    setSelectedTab(value);

    navigate({
      search: prev => ({ ...prev, tab: value }),
    });
  };

  if (authLoading || isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!canRead) {
    return <Navigate to='/clients' />;
  }

  if (!client) {
    return <div>Cliente no encontrado</div>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction='row' justifyContent='space-between'>
        <Breadcrumbs size='sm'>
          <Link to='/clients'>Clientes</Link>
          <Typography>{client?.name}</Typography>
        </Breadcrumbs>

        {canWrite && (
          <Stack direction='row' gap={1}>
            <CustomerDepositModal clientId={client.id} />
            <MultiplePaymentsModal clientId={client.id} />
            <CreateEditClientModal client={client} />
            {/* <DeleteClientModal id={client?.id.toString()} name={client?.name} /> */}
          </Stack>
        )}
      </Stack>

      <Tabs
        value={selectedTab}
        onChange={(_, value) => {
          handleTabChange(Number(value));
        }}
      >
        <TabList>
          <Tab>Información</Tab>
          <Tab>Compras</Tab>
          <Tab>Movimientos</Tab>
        </TabList>

        <TabPanel value={0}>
          <ClientInformation clientId={client.id} />
        </TabPanel>
        <TabPanel value={1}>
          <SellTable
            clientId={client.id}
            showFilters={false}
            emptyMessage='No hay compras para este cliente'
            errorTitle='Error al cargar las compras del cliente'
          />
        </TabPanel>
        <TabPanel value={2}>
          <LedgerTable
            initialFilters={{ clientId: client.id.toString() }}
            showFilters={false}
            emptyMessage='No hay movimientos registrados'
            errorTitle='Error al cargar los movimientos'
          />
        </TabPanel>
        <TabPanel value={3}>TBD</TabPanel>
      </Tabs>
    </Box>
  );
}
